// Right-side peek at the value graph, shown alongside every step workspace.
// One state: the dot map — the graph's true silhouette on ghost geography.
// Newly promoted nodes ping in place on the map (ring + zone glow via
// usePulseNodes); nothing swaps. Collapses to a thin edge tab (same pattern
// as the AI panel) — the top bar keeps a single "Graph" affordance for
// full-screen. Full-screen graph is unchanged.

import { Maximize2, X, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GraphDotMap } from '@/components/panels/GraphDotMap';
import { usePulseNodes } from '@/hooks/use-pulse-nodes';
import { useUIStore } from '@/stores';

export function GraphPeekPanel({ projectId }: { projectId: string }) {
  const setViewMode = useUIStore((s) => s.setViewMode);
  const isOpen = useUIStore((s) => s.isGraphPeekOpen);
  const setIsGraphPeekOpen = useUIStore((s) => s.setIsGraphPeekOpen);
  // Runs while collapsed too, so node-id tracking stays warm and re-opening
  // the panel doesn't pulse everything that arrived while it was shut.
  const pulseNodeIds = usePulseNodes(projectId);

  if (!isOpen) {
    return (
      <div className="hidden md:flex border-l flex-col items-center py-4 px-1 bg-card">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsGraphPeekOpen(true)}
          className="text-muted-foreground"
          title="Show live graph"
          aria-label="Show live graph"
        >
          <Network className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <aside className="hidden md:flex w-80 xl:w-96 shrink-0 border-l bg-card flex-col" aria-label="Live graph">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Network className="h-4 w-4 text-muted-foreground" />
          Live graph
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode('graph')}
            title="Open full graph"
            aria-label="Open full graph"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsGraphPeekOpen(false)}
            title="Hide live graph"
            aria-label="Hide live graph"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <GraphDotMap projectId={projectId} pulseNodeIds={pulseNodeIds} />
      </div>
    </aside>
  );
}
